import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles } from "lucide-react";

/**
 * AdminSbtSEO — one-stop editor for the marketing site's SEO / AEO
 * metadata. Backed by CMS section `seo_meta`. Every field here is
 * consumed by:
 *   • src/components/SEOHead.astro (title / description / keywords
 *     / og image / JSON-LD schemas)
 *   • src/components/FAQBlock.astro (visible FAQ + FAQPage schema)
 *   • public/llms.txt + robots.txt (kept in sync manually — this
 *     editor drives per-page facts only)
 *
 * Layout: two sub-tabs — Global (brand-wide sameAs, org details,
 * global keywords) and Per-page (title/description/keywords/FAQs
 * for each of the 9 static pages).
 */

const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || "";

const PAGES = [
  { slug: "home",        label: "Home"                  },
  { slug: "about",       label: "About Company"         },
  { slug: "services",    label: "Services"              },
  { slug: "media",       label: "Owned Media"           },
  { slug: "media-kit",   label: "Media Kit"             },
  { slug: "market-plus", label: "Market Plus"           },
  { slug: "resources",   label: "Resources"             },
  { slug: "careers",     label: "Careers"               },
  { slug: "contact",     label: "Contact Us"            },
];

const EMPTY = {
  global: { brand: "SBT Research", founding_year: "", logo_url: "", phone: "", email: "", address: {country:"",region:""}, same_as: [], keywords_global: [] },
  pages: {},
};

export default function AdminSbtSEO() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePage, setActivePage] = useState(PAGES[0].slug);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/sbt-site/sections/seo_meta`);
      const j = await r.json();
      const d = j?.data || EMPTY;
      // Ensure every static page has a stub so the editor never crashes
      const pages = { ...(d.pages || {}) };
      PAGES.forEach((p) => {
        if (!pages[p.slug]) pages[p.slug] = { title: "", description: "", keywords: [], og_image: "", faqs: [] };
      });
      setData({ global: { ...EMPTY.global, ...(d.global || {}) }, pages });
    } catch (e) {
      toast.error("Failed to load SEO settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/admin/sbt-site/sections/seo_meta`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ data }),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("SEO settings saved — Astro rebuild queued");
    } catch (e) {
      toast.error("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-10 text-center text-slate-500">Loading SEO settings…</div>;

  const page = data.pages[activePage] || {};
  const setPageField = (field, val) => {
    setData({ ...data, pages: { ...data.pages, [activePage]: { ...page, [field]: val } } });
  };
  const setGlobalField = (field, val) => {
    setData({ ...data, global: { ...data.global, [field]: val } });
  };

  return (
    <div className="space-y-6" data-testid="admin-sbt-seo">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-600" /> SEO &amp; AEO Editor
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Title, description, keywords, OG image and FAQs for every marketing page. Also emits Organization / WebSite / BreadcrumbList / FAQPage JSON-LD so ChatGPT, Perplexity, Google AI Overviews, Gemini and Bing Copilot can cite you.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="seo-save-btn">
          {saving ? "Saving…" : "Save & Publish"}
        </Button>
      </div>

      <Tabs defaultValue="pages" className="w-full">
        <TabsList>
          <TabsTrigger value="pages"  data-testid="seo-tab-pages">Per-Page SEO</TabsTrigger>
          <TabsTrigger value="global" data-testid="seo-tab-global">Global &amp; Organization</TabsTrigger>
        </TabsList>

        {/* ─── PER-PAGE ─── */}
        <TabsContent value="pages" className="mt-4">
          <div className="flex gap-2 flex-wrap mb-4">
            {PAGES.map((p) => (
              <button
                key={p.slug}
                onClick={() => setActivePage(p.slug)}
                data-testid={`seo-page-${p.slug}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition ${
                  activePage === p.slug
                    ? "bg-cyan-600 border-cyan-600 text-white"
                    : "bg-white border-slate-200 text-slate-600 hover:border-cyan-400"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">{PAGES.find((p) => p.slug === activePage)?.label} — SEO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Meta Title <span className="text-slate-400">(50–65 chars ideal)</span></Label>
                <Input value={page.title || ""} onChange={(e) => setPageField("title", e.target.value)} data-testid="seo-title" />
                <div className="text-xs text-slate-400 mt-1">{(page.title || "").length} characters</div>
              </div>
              <div>
                <Label className="text-xs">Meta Description <span className="text-slate-400">(140–160 chars ideal)</span></Label>
                <Textarea rows={3} value={page.description || ""} onChange={(e) => setPageField("description", e.target.value)} data-testid="seo-description" />
                <div className="text-xs text-slate-400 mt-1">{(page.description || "").length} characters</div>
              </div>
              <div>
                <Label className="text-xs">Keywords <span className="text-slate-400">(comma-separated)</span></Label>
                <Input
                  value={(page.keywords || []).join(", ")}
                  onChange={(e) => setPageField("keywords", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                  data-testid="seo-keywords"
                />
              </div>
              <div>
                <Label className="text-xs">OG / Twitter Card Image URL <span className="text-slate-400">(optional — falls back to brand logo)</span></Label>
                <Input value={page.og_image || ""} onChange={(e) => setPageField("og_image", e.target.value)} data-testid="seo-og-image" />
              </div>

              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">FAQs on this page</div>
                    <div className="text-xs text-slate-500">Renders as visible &lt;details&gt; block AND FAQPage JSON-LD. This is the #1 lever for Perplexity + Google AI Overviews citations.</div>
                  </div>
                  <Button variant="outline" size="sm" data-testid="seo-add-faq" onClick={() => setPageField("faqs", [...(page.faqs || []), { q: "", a: "" }])}>
                    <Plus className="w-4 h-4 mr-1" /> Add FAQ
                  </Button>
                </div>
                <div className="space-y-3">
                  {(page.faqs || []).map((f, i) => (
                    <div key={i} className="border rounded-lg p-3 bg-slate-50" data-testid={`seo-faq-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-slate-500">FAQ #{i + 1}</div>
                        <button
                          onClick={() => setPageField("faqs", (page.faqs || []).filter((_, k) => k !== i))}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`seo-faq-delete-${i}`}
                          aria-label="Delete FAQ"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <Label className="text-xs">Question</Label>
                      <Input
                        value={f.q}
                        onChange={(e) => {
                          const next = [...(page.faqs || [])];
                          next[i] = { ...next[i], q: e.target.value };
                          setPageField("faqs", next);
                        }}
                        data-testid={`seo-faq-q-${i}`}
                        className="mb-2"
                      />
                      <Label className="text-xs">Answer</Label>
                      <Textarea
                        rows={3}
                        value={f.a}
                        onChange={(e) => {
                          const next = [...(page.faqs || [])];
                          next[i] = { ...next[i], a: e.target.value };
                          setPageField("faqs", next);
                        }}
                        data-testid={`seo-faq-a-${i}`}
                      />
                    </div>
                  ))}
                  {(!page.faqs || !page.faqs.length) && (
                    <div className="text-xs text-slate-400 italic border border-dashed rounded-lg p-6 text-center">No FAQs yet. Add 3–5 to dramatically boost AI-answer citations.</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── GLOBAL ─── */}
        <TabsContent value="global" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Organization &amp; Global SEO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Brand name</Label><Input value={data.global.brand || ""} onChange={(e) => setGlobalField("brand", e.target.value)} data-testid="seo-brand" /></div>
                <div><Label className="text-xs">Founding year</Label><Input value={data.global.founding_year || ""} onChange={(e) => setGlobalField("founding_year", e.target.value)} data-testid="seo-founded" /></div>
                <div><Label className="text-xs">Contact email</Label><Input value={data.global.email || ""} onChange={(e) => setGlobalField("email", e.target.value)} data-testid="seo-email" /></div>
                <div><Label className="text-xs">Contact phone</Label><Input value={data.global.phone || ""} onChange={(e) => setGlobalField("phone", e.target.value)} data-testid="seo-phone" /></div>
                <div><Label className="text-xs">Country code (ISO)</Label><Input value={data.global.address?.country || ""} onChange={(e) => setGlobalField("address", { ...(data.global.address || {}), country: e.target.value })} data-testid="seo-country" /></div>
                <div><Label className="text-xs">Region</Label><Input value={data.global.address?.region || ""} onChange={(e) => setGlobalField("address", { ...(data.global.address || {}), region: e.target.value })} data-testid="seo-region" /></div>
              </div>
              <div>
                <Label className="text-xs">Logo URL (also used as fallback OG image)</Label>
                <Input value={data.global.logo_url || ""} onChange={(e) => setGlobalField("logo_url", e.target.value)} data-testid="seo-logo-url" />
              </div>
              <div>
                <Label className="text-xs">Social profile URLs <span className="text-slate-400">(sameAs — emits &lt;Organization sameAs&gt; schema so AI engines link social identities to the brand)</span></Label>
                <Textarea
                  rows={5}
                  placeholder="One URL per line — LinkedIn, X/Twitter, Facebook, YouTube, Instagram, Crunchbase, GitHub…"
                  value={(data.global.same_as || []).join("\n")}
                  onChange={(e) => setGlobalField("same_as", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                  data-testid="seo-same-as"
                />
              </div>
              <div>
                <Label className="text-xs">Global keyword pool <span className="text-slate-400">(appended to every page&apos;s keywords meta)</span></Label>
                <Textarea
                  rows={4}
                  placeholder="Comma-separated — e.g. AI leads, Intent leads, Demand Generation, MQL, ABM agency…"
                  value={(data.global.keywords_global || []).join(", ")}
                  onChange={(e) => setGlobalField("keywords_global", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                  data-testid="seo-global-keywords"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
